import { apiClient, API_PRX } from '@indipia/shared';

/** 활동지역 시군구 (코드 + 표시명) */
export interface PartnerSigungu {
  code: string;
  name: string;
}

export interface PartnerItem {
  partnerId: number;
  partnerName: string;
  mobile: string;
  email: string | null;
  /** 1차 역할 코드 (공통코드 E01_PARTNER_ROLE: CONSTRUCTION/MATERIAL/BOTH) */
  partnerRole: string | null;
  /** 담당자명 (자재 역할) */
  contactName: string | null;
  /** 주소 (자재 역할) */
  partnerAddress: string | null;
  /** 세부취급품목 자유입력 (자재 역할) */
  dealItem: string | null;
  /** 주작업공종 코드 (단일) */
  specialtyTypes: string | null;
  /** 파트너 유형 (공통코드 E01_PARTNER_TYPE: FOREMAN/COMPANY). NULL=미지정 */
  partnerType: string | null;
  /** 거래 상태 (공통코드 E01_PARTNER_DEAL_STATUS: ACTIVE/SUSPENDED) */
  dealStatus: string | null;
  /** 연락 방식 ('Y'=알림톡 발송-회신 / 'N'=결과만 직접등록) */
  commYn?: string;
  /** 부작업공종 코드 목록 */
  subWorkTypes: string[];
  /** 활동지역 시군구 목록 */
  sigungus: PartnerSigungu[];
  /** 전문분야 태그 목록 */
  tags: string[];
  /** 평균별점 (사업장 내 현장별 평가 평균, 평가 없으면 null) */
  avgRating: number | null;
  /** 평가 건수 */
  reviewCount: number;
  partnerBizYn: string;
  partnerBizNo: string | null;
  partnerBizName: string | null;
  partnerRepName: string | null;
  kakaoChannelId: string | null;
  kakaoLinkedYn: string;
  memo: string | null;
  createdById: number;
  createdByName: string;
  createdAt: string;
  lastConstructionSiteName: string | null;
  lastConstructionDate: string | null;
}

/** 중복 확인 — 개인=mobile(전화번호) / 사업자=partnerBizNo 중 하나 전달 */
export async function checkPartnerExists(
  params: { mobile?: string; partnerBizNo?: string },
  excludePartnerId?: number,
): Promise<boolean> {
  const res = await apiClient.get(`${API_PRX.MEMBER}/partners/exists`, {
    params: { ...params, ...(excludePartnerId ? { excludePartnerId } : {}) },
  });
  return (res.data.data?.exists ?? false) as boolean;
}

/**
 * 파트너 목록 조회.
 * role 필터: CONSTRUCTION=시공·둘다(작업요청 후보), MATERIAL=자재·둘다, BOTH=둘다만, 생략 시 전체.
 */
export async function fetchPartners(keyword?: string, role?: string): Promise<PartnerItem[]> {
  const params: Record<string, string> = {};
  if (keyword) params.keyword = keyword;
  if (role) params.role = role;
  const res = await apiClient.get(`${API_PRX.MEMBER}/partners`, {
    params: Object.keys(params).length ? params : undefined,
  });
  return (res.data.data ?? []) as PartnerItem[];
}

export async function createPartner(payload: {
  partnerName: string;
  mobile: string;
  email?: string | null;
  partnerRole?: string | null;
  contactName?: string | null;
  partnerAddress?: string | null;
  dealItem?: string | null;
  specialtyTypes?: string;
  partnerType?: string | null;
  dealStatus?: string | null;
  commYn?: string;
  subWorkTypes?: string[];
  sigungus?: PartnerSigungu[];
  tags?: string[];
  partnerBizYn?: string;
  partnerBizNo?: string | null;
  partnerBizName?: string | null;
  partnerRepName?: string | null;
  memo?: string | null;
}): Promise<number> {
  const res = await apiClient.post(`${API_PRX.MEMBER}/partners`, payload);
  return res.data.data.partner_id as number;
}

/** 시공 상태(TS_SCHEDULE_TASK.TASK_PROGRESS_STATUS) — INCOMPLETE/COMPLETE 2값, NULL=현장보고 미등록 */
export type ConstructionStatus = 'INCOMPLETE' | 'COMPLETE';

/**
 * 현장 카드 안에 나열되는 개별 시공 건(작업건). 평가는 현장+공종(작업건) 단위이므로
 * 이 건의 공종(workType)에 걸린 평가(rating/evalMemo)를 함께 내려준다(평가 없으면 둘 다 null).
 */
export interface ConstructionHistoryItem {
  constructionId: number;
  workType: string | null;
  periodFrom: string | null;
  periodTo: string | null;
  status: ConstructionStatus | null;
  rating: number | null;
  evalMemo: string | null;
  /** 현장이 완료면 true — 평가 입력·수정·삭제 모두 잠김. 서버가 내려주지 않으면(undefined) 잠그지 않음 */
  reviewLocked?: boolean;
}

/** C1 응답의 현장 단위 그룹 — items(작업건)마다 평가가 걸린다(현장+공종 단위) */
export interface SiteConstructionHistory {
  siteId: number;
  siteName: string | null;
  customerName: string | null;
  items: ConstructionHistoryItem[];
}

export interface ConstructionHistoryResponse {
  sites: SiteConstructionHistory[];
  totalCount: number;
  hasNext: boolean;
}

/** C1 — 반장님 시공이력(현장별 그룹, 현장 종합평가 포함) 조회 */
export async function fetchPartnerConstructionHistory(
  partnerId: number,
  page = 0,
  size = 20,
): Promise<ConstructionHistoryResponse> {
  const res = await apiClient.get(`${API_PRX.MEMBER}/partners/${partnerId}/construction-history`, {
    params: { page, size },
  });
  return res.data.data;
}

/** C2 — 현장+공종(작업건) 단위 평가 등록/수정(upsert). workTypeCode가 키의 일부(필수) */
export async function upsertSiteReview(
  partnerId: number,
  siteId: number,
  payload: { workTypeCode: string; rating?: number | null; evalMemo?: string | null },
): Promise<void> {
  await apiClient.put(`${API_PRX.MEMBER}/partners/${partnerId}/sites/${siteId}/review`, payload);
}

/**
 * 시공파트너 지표 (sv-schedule GET /schedule/partners 응답 중 목록 병합에 쓰는 값).
 * 보고준수·하자보고는 정수 %(0~100), 값이 없거나(분모 0) 집계 조회 실패면 null.
 */
export interface PartnerMetrics {
  partnerId: number;
  /** 직전 12개월 확정 배정 건수 */
  collabCount: number;
  /** 일정회신 2시간 내 % */
  replyWithinTwoHoursPercent: number | null;
  reportComplianceRate: number | null;
  defectReportRate: number | null;
}

/** 시공파트너 지표 목록 조회 — 목록(fetchPartners)과 partnerId 로 병합해 쓴다 (sv-schedule) */
export async function fetchPartnerMetrics(): Promise<PartnerMetrics[]> {
  const res = await apiClient.get(`/schedule/partners`);
  return (res.data.data ?? []) as PartnerMetrics[];
}

/** 일정확인 상태 — 공종(작업건)별 마지막 시도 기준 */
export type PartnerConfirmState = 'REPLIED_CONFIRMED' | 'REPLIED_DECLINED' | 'WAITING' | 'EXPIRED' | 'CLOSED';

/** 파트너별 일정확인 이력 요약 1건 (taskId = 시공이력 constructionId). 일정확인이 없는 공종은 응답에서 생략 */
export interface PartnerConfirmHistoryItem {
  taskId: number;
  state: PartnerConfirmState;
  sentDtm: string | null;
  respondedDtm: string | null;
  replyExpireDtm: string | null;
  /** 회신 건만(분, floor). 이상 행은 null */
  elapsedMinutes: number | null;
  /** 회신 건만. 회신이 발송+2시간 이내(경계 포함)면 true */
  withinTwoHours: boolean | null;
}

/** 파트너별 일정확인 이력 요약 조회 (sv-schedule GET /schedule/partners/{partnerId}/confirm-history) */
export async function fetchPartnerConfirmHistory(partnerId: number): Promise<PartnerConfirmHistoryItem[]> {
  const res = await apiClient.get(`/schedule/partners/${partnerId}/confirm-history`);
  return (res.data.data ?? []) as PartnerConfirmHistoryItem[];
}

/**
 * 이 시공반장이 주시공파트너로 등록된 공종코드 목록 조회 (sv-schedule)
 * GET /schedule/main-partners/for-partner?partnerId
 */
export async function fetchMainPartnerWorkTypes(partnerId: number): Promise<string[]> {
  const res = await apiClient.get(`/schedule/main-partners/for-partner`, {
    params: { partnerId },
  });
  return (res.data.data ?? []) as string[];
}

export async function updatePartner(
  partnerId: number,
  payload: {
    action: 'update' | 'delete';
    partnerName?: string;
    mobile?: string;
    email?: string | null;
    partnerRole?: string | null;
    contactName?: string | null;
    partnerAddress?: string | null;
    dealItem?: string | null;
    specialtyTypes?: string;
    partnerType?: string | null;
    dealStatus?: string | null;
    commYn?: string | null;
    subWorkTypes?: string[];
    sigungus?: PartnerSigungu[];
    tags?: string[];
    partnerBizYn?: string;
    partnerBizNo?: string | null;
    partnerBizName?: string | null;
    partnerRepName?: string | null;
    memo?: string | null;
  },
): Promise<void> {
  await apiClient.patch(`${API_PRX.MEMBER}/partners/${partnerId}`, payload);
}
